import moment from "moment"
import { DeliveryOptionInterface } from "../models/delivery-options"
import { GetUpsellProductsPayload } from "../models/get-upsell-products-payload"
import { AuthenticateRequest } from "../models/email-authenticate-request"
import { EmailAuthenticateResponse } from "../models/email-authenticate-response"
import { ErrorItem } from "../models/error"
import { SignupInfo } from "../models/signup-info-item"
import {
  AddCreditCardResponse,
  AddCardIntentType,
  GetCreditCardsResponse,
} from "./api-types"
import { CheckAddressPayload } from "../models/check-address-payload"
import {
  EmailVerificationConfirmRequest,
  EmailVerificationRequest,
} from "../models/email-verification"
import { SignupLightPayload, SignupPayload } from "../models/sign-up-form"
import { Restaurant, RestaurantListItem } from "../models/restaurant"
import { useFetchRequest } from "./request"
import { AccountResponse, AccountSessionResponse } from "../models/user"
import { InitOrderRequestData } from "../models/init-order-payload"
import { CheckoutOrder, Order, SimpleOrder, ActiveOrder } from "../models/order"
import {
  AddressListSuccessResponse,
  AddressType,
  CheckAddressSuccessResponse,
} from "../models/address"
import { RecommendedProduct } from "../models/recommended-product"
import { getModifiedOpeningHours } from "./opening-hours-service"
import { UpdateOrderRequestData } from "../models/update-order-payload"
import { ApplyBenefitOnOrderResponse } from "../models/applyBenefitOnOrderResponse"
import { getCateringSpec } from "../context/catering-context/CateringProvider"
import { Price } from "../models/price"
import {
  AdyenPaymentDetails,
  AdyenPaymentMethodsResponse,
} from "../models/adyen-payment-methods"
import {
  AdditionalDetailsData,
  PaymentData,
  PaymentResponseData,
} from "@adyen/adyen-web"
import {
  PaymentMethods,
  ProviderName,
  SupportedPaymentMethods,
} from "../models/payment-methods"
import { UpdateProfilePayload } from "../models/update-profile-payload"
import { ProductLabel } from "../models/product-label"

const VENUES_PATH = process.env.GATSBY_VENUES_PATH || "venues-list-all"
const API_PREFIX = "/api/v0"

export const useAnonymousAPI = () => {
  const fetchRequestAnonymous = useFetchRequest({ anonymous: true })

  return {
    restaurants: {
      async list(payload?: {
        longitude?: number
        latitude?: number
        showHidden?: boolean
      }) {
        const searchParams = new URLSearchParams()
        searchParams.set("latitude", payload?.latitude?.toString() || "55")
        searchParams.set("longitude", payload?.longitude?.toString() || "12")
        if (payload?.showHidden !== undefined) {
          searchParams.set("showHidden", payload.showHidden.toString())
        }
        let path = `${API_PREFIX}/${VENUES_PATH}?${searchParams.toString()}`
        const data = await fetchRequestAnonymous<{
          venues: {
            items: RestaurantListItem[]
          }
        }>("GET", path)
        return data.venues.items.map((item) => {
          const cateringSpec = getCateringSpec(item.shop_id)

          // Remodelling of opening hours
          const modifiedOpeningHours = getModifiedOpeningHours(
            item.legacy_opening_hours.regular,
            item.legacy_opening_hours.special,
            cateringSpec
          )

          let open_for_orders = item.open_for_orders

          // If no opening hours at all, we set open_for_orders to false
          const allOpeningHours = modifiedOpeningHours
            .map((oh) => oh.openingHours)
            .flat()
          if (allOpeningHours.length === 0) {
            open_for_orders = false
          }

          // Not propogating modifiedOpeningHours, as it's not needed
          return {
            ...item,
            open_for_orders,
          }
        })
      },
    },
    guest: {
      guestSignin(): Promise<AccountSessionResponse> {
        const path = `${API_PREFIX}/auth/guest`
        return fetchRequestAnonymous("POST", path)
      },
    },
    auth: {
      signin(
        payload: AuthenticateRequest
      ): Promise<{ data: EmailAuthenticateResponse }> {
        const path = `${API_PREFIX}/auth/username`
        return fetchRequestAnonymous("POST", path, { data: payload })
      },

      requestEmailVerification(
        payload: EmailVerificationRequest
      ): Promise<string> {
        const path = `${API_PREFIX}/email_verify`
        return fetchRequestAnonymous("POST", path, { data: payload })
      },

      confirmEmailVerification(
        payload: EmailVerificationConfirmRequest
      ): Promise<string | ErrorItem> {
        const path = `${API_PREFIX}/email_confirm`
        return fetchRequestAnonymous("POST", path, { data: payload })
      },

      signup(payload: SignupPayload): Promise<object> {
        const path = `${API_PREFIX}/signup`
        return fetchRequestAnonymous("POST", path, { data: payload })
      },

      signupLight(payload: SignupLightPayload): Promise<object> {
        const path = `${API_PREFIX}/signup-light`
        return fetchRequestAnonymous("POST", path, { data: payload })
      },

      resetPassword(payload: { email: string }): Promise<string | ErrorItem> {
        const path = `${API_PREFIX}/password-recovery/reset`
        return fetchRequestAnonymous("POST", path, { data: payload })
      },

      signupInfo(): Promise<SignupInfo> {
        const path = `${API_PREFIX}/signup-info`
        return fetchRequestAnonymous("GET", path)
      },
    },
    checkout: {
      retrieveOrderByDisplayId(displayId: string) {
        const path = `${API_PREFIX}/tracking-orders/${displayId}`

        return fetchRequestAnonymous<{ data: SimpleOrder }>("GET", path)
      },
    },
    other: {
      // TODO
      async getCountryCode(): Promise<string> {
        const getThreeLetterCountryCode = (twoLetter: string) => {
          if (twoLetter === "NO") {
            return "nor"
          }
          if (twoLetter === "IS") {
            return "isl"
          }
          if (twoLetter === "SE") {
            return "swe"
          }
          return "dnk"
        }

        const path = `${API_PREFIX}/signup-info`
        try {
          const response = await fetchRequestAnonymous<any>("GET", path)
          const signup_info = response?.data?.signup_info
          const twoLetter = signup_info && signup_info[0]?.country_code
          const threeLetterCountryCode = getThreeLetterCountryCode(twoLetter)
          return threeLetterCountryCode
        } catch {
          return "dnk"
        }
      },
    },
    passwordRecovery: {
      resetPassword({
        organizationKeyName,
        password,
        token,
      }: {
        password: string
        token: string
        organizationKeyName: string
      }) {
        const url = `/api/external/user/${organizationKeyName}/password-recovery/change`
        return fetchRequestAnonymous("POST", url, {
          data: { password, token },
          headers: {
            "Content-Type": "application/json",
          },
        })
      },

      validateToken({
        token,
        organizationKeyName,
      }: {
        token: string
        organizationKeyName: string
      }) {
        const url = `/api/external/user/${organizationKeyName}/password-recovery/validate`
        return fetchRequestAnonymous<{ email: string }>("POST", url, {
          data: { token },
          headers: {
            "Content-Type": "application/json",
          },
        })
      },
    },
  }
}

export const useAPI = () => {
  const fetchRequest = useFetchRequest({ anonymous: false })

  return {
    restaurants: {
      async retrieveTimeslotOptions(
        addressUrl: string,
        timeslots_for: moment.Moment,
        deliveryAddress: AddressType | null
      ) {
        const params = new URLSearchParams()
        if (deliveryAddress?.latitude) {
          params.set("latitude", deliveryAddress.latitude.toString())
        }
        if (deliveryAddress?.longitude) {
          params.set("longitude", deliveryAddress.longitude.toString())
        }
        // API returns all values if the timeslots_for param exists - so append it only if some other day
        if (!timeslots_for.isSame(moment(), "day")) {
          params.set(
            "timeslots_for",
            timeslots_for.format("YYYY-MM-DDT00:00:00Z")
          )
        }

        const url = `${addressUrl}?${params.toString()}`

        const data = await fetchRequest("GET", url) // TODO return value type
        const deliveryOptionsArray: DeliveryOptionInterface[] =
          data["delivery-options"]

        return deliveryOptionsArray
      },
      listProductLabels({ shopId }: { shopId?: string }) {
        const path = `${API_PREFIX}/shops/${shopId}/product-labels`
        return fetchRequest<ProductLabel[]>("GET", path)
      },
      async retrieve(
        slug: string,
        params?: {
          requested_time?: string
          locale?: string
        }
      ): Promise<Restaurant> {
        let path = `${API_PREFIX}/venues/${slug}`
        const searchParams = new URLSearchParams()
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value) {
              searchParams.set(key, value)
            }
          })
          path += `?${searchParams.toString()}`
        }

        let data = await fetchRequest<{ data: Restaurant }>("GET", path)

        const cateringSpec = getCateringSpec(data.data.shop.id)

        // Remodelling of opening hours
        const modifiedOpeningHours = getModifiedOpeningHours(
          data.data.legacy_opening_hours.regular,
          data.data.legacy_opening_hours.special,
          cateringSpec
        )

        let open_for_orders = data.data.open_for_orders

        // If no opening hours at all, we set open_for_orders to false
        const allOpeningHours = modifiedOpeningHours
          .map((oh) => oh.openingHours)
          .flat()
        if (allOpeningHours.length === 0) {
          open_for_orders = false
        }

        return {
          ...data.data,
          open_for_orders,
          modifiedOpeningHours,
        }
      },
    },

    profile: {
      withdrawTermConsent({ termVersionId }: { termVersionId: string }) {
        const path = `${API_PREFIX}/terms/${termVersionId}/withdraw`
        return fetchRequest("POST", path)
      },
      acceptTerm({ termVersionId }: { termVersionId: string }) {
        const path = `${API_PREFIX}/terms/${termVersionId}/accept`
        return fetchRequest("POST", path)
      },
    },
    checkout: {
      init({ path, payload }: InitOrderRequestData): Promise<CheckoutOrder> {
        return fetchRequest("POST", path, {
          data: payload,
          followRedirect: true,
        })
      },

      updateAddress(addressUrl: any) {
        return fetchRequest("POST", addressUrl)
      },

      updateOrder({
        payload,
        path,
      }: UpdateOrderRequestData): Promise<CheckoutOrder> {
        return fetchRequest("POST", path, {
          data: payload,
          followRedirect: true,
        })
      },

      pay(paymentMethod: any, order: CheckoutOrder | undefined) {
        const payUrl = order?.order?._links?.pay
        if (!payUrl) {
          return
        }
        return fetchRequest("POST", payUrl, {
          data: paymentMethod,
          followRedirect: true,
        })
      },

      completeSca(order: Order) {
        const path = order.intents![0]._links.confirm! // TODO links type check
        return fetchRequest<CheckoutOrder>("POST", path)
      },

      listAdyenPaymentMethods({ amount, url }: { amount: Price; url: string }) {
        return fetchRequest<AdyenPaymentMethodsResponse>("POST", url, {
          data: { amount },
        })
      },

      listPaymentMethods({
        orderId,
        supportedMethods,
      }: {
        orderId: string
        supportedMethods: SupportedPaymentMethods
      }) {
        const payload = {
          supported_methods: supportedMethods,
        }

        const path = `${API_PREFIX}/orders/${orderId}/payment-methods`
        return fetchRequest<PaymentMethods>("POST", path, {
          data: { ...payload },
        })
      },

      retrieveOrderById(orderId: any, locale?: string) {
        if (!orderId) {
          return
        }
        let path = `${API_PREFIX}/orders/${orderId}`
        if (locale) {
          const searchParams = new URLSearchParams()
          searchParams.set("locale", locale)
          path += `?${searchParams.toString()}`
        }
        return fetchRequest<CheckoutOrder>("GET", path, {
          ...(locale && { headers: { "Accept-Language": locale } }),
        })
      },

      retrieveOrder(order: Order): Promise<CheckoutOrder> {
        let path = order._links.self
        return fetchRequest("GET", path)
      },

      removeFromOrder(path: string) {
        return fetchRequest("DELETE", path)
      },

      applyVoucher(path: string) {
        return fetchRequest("PUT", path)
      },

      removeVoucherOrMembercard(path: string) {
        return fetchRequest("DELETE", path)
      },

      removeGiftCard(path: string) {
        return fetchRequest("DELETE", path)
      },

      applyBenefitOnOrder({
        code,
        orderId,
      }: {
        code: string
        orderId: string
      }) {
        const path = `${API_PREFIX}/orders/${orderId}/redeem-code`
        return fetchRequest<ApplyBenefitOnOrderResponse>("POST", path, {
          data: { code },
        })
      },
    },
    orders: {
      listActiveOrders() {
        const path = `${API_PREFIX}/me/active-orders`
        return fetchRequest<{ "active-orders": ActiveOrder[] }>("GET", path)
      },
    },
    addresses: {
      list(): Promise<AddressListSuccessResponse> {
        let path = `${API_PREFIX}/me/addresses`
        return fetchRequest("GET", path)
      },

      create(payload: AddressType) {
        let path = `${API_PREFIX}/me/addresses`
        return fetchRequest("POST", path, { data: payload })
      },

      check({
        path,
        address,
        preOrderFor,
      }: CheckAddressPayload): Promise<CheckAddressSuccessResponse> {
        return fetchRequest("POST", path, {
          data: {
            address_line: address.address_line,
            city: address.city,
            country_code: address.country_code,
            postcode: address.postcode,
            longitude: address.longitude,
            latitude: address.latitude,
            pre_order_for: preOrderFor,
          },
        })
      },
      async delete(id: string): Promise<boolean> {
        const path = `${API_PREFIX}/me/addresses/${id}`
        await fetchRequest("DELETE", path)
        return true
      },
    },
    payments: {
      cancel(cancelPath: string) {
        return fetchRequest<CheckoutOrder>("POST", cancelPath)
      },

      makeAdyenPayment({
        url,
        data,
      }: {
        url: string
        data: PaymentData
      }): Promise<PaymentResponseData> {
        return fetchRequest("POST", url, {
          data,
        })
      },

      getAdyenPaymentDetails({
        redirectResult,
        paymentDetailsUrl,
      }: {
        redirectResult: string
        paymentDetailsUrl: string
      }) {
        const params = new URLSearchParams()
        if (redirectResult) {
          params.set("redirectResult", redirectResult)
        }
        const url = `${paymentDetailsUrl}?${params.toString()}`
        return fetchRequest<AdyenPaymentDetails>("GET", url)
      },

      postAdyenPaymentDetails({
        data,
        paymentDetailsUrl,
      }: {
        paymentDetailsUrl: string
        data: AdditionalDetailsData["data"]
      }) {
        return fetchRequest<AdyenPaymentDetails>("POST", paymentDetailsUrl, {
          data,
        })
      },

      createIntent(data: {
        success_url: string | null
        cancel_url: string | null
        supported_providers: ProviderName[]
      }) {
        const path = `${API_PREFIX}/ps/create-intent`
        return fetchRequest<AddCardIntentType>("POST", path, {
          data,
        })
      },

      addCreditCard(intentId: string): Promise<AddCreditCardResponse> {
        const path = `${API_PREFIX}/ps/creditcard-add`
        return fetchRequest("POST", path, {
          data: {
            gateway: "stripe", // addCreditCard always called with Stripe
            intent_id: intentId,
          },
        })
      },

      getCreditCards() {
        const path = `${API_PREFIX}/me/credit-cards`
        return fetchRequest<GetCreditCardsResponse>("GET", path)
      },

      deleteCreditCard(id: string) {
        const path = `${API_PREFIX}/me/credit-cards/${id}`
        return fetchRequest("DELETE", path)
      },
    },

    auth: {
      me(): Promise<AccountResponse> {
        return fetchRequest("GET", `${API_PREFIX}/me`)
      },

      updateProfile({ data }: { data: UpdateProfilePayload }) {
        const path = `${API_PREFIX}/me`
        return fetchRequest<object>("POST", path, {
          data,
          followRedirect: true,
        })
      },

      signout(): Promise<string | ErrorItem> {
        const path = `${API_PREFIX}/signout`
        return fetchRequest("POST", path)
      },
    },

    recommendedProducts: {
      async getUpsellProducts(
        payload: GetUpsellProductsPayload
      ): Promise<RecommendedProduct[]> {
        const { shopId, locale, ...rest } = payload
        const searchParams = new URLSearchParams()
        if (locale) {
          searchParams.set("locale", locale)
        }
        const basePath = `${API_PREFIX}/shops/${shopId}/recommended-products`
        const query = searchParams.toString()
        const path = query ? `${basePath}?${query}` : basePath
        const data = await fetchRequest("POST", path, { data: rest })
        return data["recommended-products"]
      },
    },
  }
}
